import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft, Send, Users, Mail, MessageSquare, History, Loader2, Eye, CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';
import UserTypeahead from '../../components/UserTypeahead';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from '../../components/ui/dialog';

/**
 * Admin Broadcast
 * Lets a super admin email / SMS (or both) a specific user, all talent,
 * all clients, all users, or a filter set.
 *
 * Intentionally single-file: every piece of state and UI is in one component
 * so an admin only has ONE page to learn. Send flow is:
 *   1. pick target  →  2. preview count  →  3. write message  →  4. send
 */

const TEMPLATES = [
  {
    id: 'save_app',
    label: 'Save the app to your phone',
    subject: 'Save the iEvntür app to your home screen',
    body_html:
      '<p>Hi {{first_name}},</p>' +
      '<p>Save the iEvntür app to your home screen so you never miss a gig.</p>' +
      '<p><strong>iPhone:</strong> Open <a href="{{app_url}}">{{app_url}}</a> in Safari → tap the Share icon → <em>Add to Home Screen</em>.</p>' +
      '<p><strong>Android:</strong> Open <a href="{{app_url}}">{{app_url}}</a> in Chrome → tap the ⋮ menu → <em>Install app</em> or <em>Add to Home screen</em>.</p>' +
      '<p>Takes 10 seconds. See you on the next event!<br/>— iEvntür team</p>',
    body_text:
      'Hi {{first_name}}, save the iEvntür app to your home screen: {{app_url}}. ' +
      'iPhone: Safari → Share → Add to Home Screen. Android: Chrome → ⋮ → Install app. — iEvntür',
  },
  {
    id: 'platform_announcement',
    label: 'Platform announcement',
    subject: 'Quick update from iEvntür',
    body_html:
      '<p>Hi {{first_name}},</p>' +
      '<p>We wanted to share a quick update:</p>' +
      '<p>[Your announcement here]</p>' +
      '<p>Visit the app: <a href="{{app_url}}">{{app_url}}</a><br/>— iEvntür team</p>',
    body_text:
      'Hi {{first_name}}, quick update from iEvntür: [your announcement]. Visit {{app_url}}',
  },
  {
    id: 'event_reminder',
    label: 'Event reminder',
    subject: 'Upcoming event — quick reminder',
    body_html:
      '<p>Hi {{first_name}},</p>' +
      '<p>A quick reminder about your upcoming event — please review your shift details in the app: <a href="{{app_url}}">{{app_url}}</a>.</p>' +
      '<p>— iEvntür team</p>',
    body_text:
      'Hi {{first_name}}, reminder about your upcoming event. Review details in the app: {{app_url}}',
  },
];

const TARGET_OPTIONS = [
  { value: 'all_talent', label: 'All Talent' },
  { value: 'all_clients', label: 'All Clients' },
  { value: 'all_users', label: 'All Users (talent + clients)' },
  { value: 'filtered', label: 'Filtered group…' },
  { value: 'users', label: 'Specific users (search)' },
];

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email only' },
  { value: 'sms', label: 'SMS only' },
  { value: 'both', label: 'Email + SMS' },
];

const AdminBroadcast = () => {
  const navigate = useNavigate();
  const [channel, setChannel] = useState('email');
  const [targetType, setTargetType] = useState('all_talent');
  const [selectedUsers, setSelectedUsers] = useState([]);  // [{id, full_name, email, ...}]
  const [filterStates, setFilterStates] = useState('');
  const [filterCities, setFilterCities] = useState('');
  const [filterRoles, setFilterRoles] = useState('talent');

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');

  const [previewStats, setPreviewStats] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [tab, setTab] = useState('compose'); // compose | history
  const [detailBroadcast, setDetailBroadcast] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openBroadcastDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/broadcast/${id}`);
      setDetailBroadcast(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load details');
    } finally {
      setDetailLoading(false);
    }
  };

  const filters = useMemo(() => {
    const f = {};
    if (filterRoles && targetType === 'filtered') f.roles = filterRoles.split(',').map(s => s.trim()).filter(Boolean);
    if (filterStates && targetType === 'filtered') f.states = filterStates.split(',').map(s => s.trim()).filter(Boolean);
    if (filterCities && targetType === 'filtered') f.cities = filterCities.split(',').map(s => s.trim()).filter(Boolean);
    return f;
  }, [filterRoles, filterStates, filterCities, targetType]);

  const buildBody = (overrideType = null) => ({
    channel,
    subject: subject || undefined,
    body_html: bodyHtml || undefined,
    body_text: bodyText || undefined,
    target_type: overrideType || targetType,
    target_user_ids: targetType === 'users' ? selectedUsers.map(u => u.id) : undefined,
    filters: targetType === 'filtered' ? filters : undefined,
  });

  const runPreview = async () => {
    setPreviewing(true);
    setPreviewStats(null);
    try {
      const { data } = await api.post('/admin/broadcast/preview', buildBody());
      setPreviewStats(data);
      toast.success(`${data.total} recipients match this target`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const doSend = async () => {
    if (!previewStats) {
      toast.error('Run preview first to confirm recipient count');
      return;
    }
    const ok = window.confirm(
      `Send to ${previewStats.total} recipients via ${channel.toUpperCase()}?\n\nThis action cannot be undone.`,
    );
    if (!ok) return;

    setSending(true);
    try {
      const { data } = await api.post('/admin/broadcast/send', buildBody());
      toast.success(data.message || 'Broadcast sent');
      if (data.skipped_count > 0) {
        toast.warning(
          `${data.skipped_count} recipient${data.skipped_count > 1 ? 's were' : ' was'} skipped because of an invalid or placeholder phone number. Check History for details.`,
          { duration: 8000 },
        );
      }
      if (data.failed_count > 0) {
        toast.error(
          `${data.failed_count} recipient${data.failed_count > 1 ? 's' : ''} failed during delivery. Check History for per-recipient error messages.`,
          { duration: 8000 },
        );
      }
      setTab('history');
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Broadcast failed');
    } finally {
      setSending(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get('/admin/broadcast/history?limit=100');
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'history') fetchHistory();
  }, [tab]);

  const applyTemplate = (tplId) => {
    const tpl = TEMPLATES.find(t => t.id === tplId);
    if (!tpl) return;
    setSubject(tpl.subject);
    setBodyHtml(tpl.body_html);
    setBodyText(tpl.body_text);
    toast.message(`Applied template: ${tpl.label}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-2 mb-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="broadcast-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/broadcast/templates')}
            data-testid="manage-templates-btn"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            Manage Templates
          </Button>
        </div>

        <h1 className="text-2xl font-semibold mb-1 flex items-center gap-2">
          <Send className="w-6 h-6 text-cyan-600" /> Broadcast
        </h1>
        <p className="text-sm text-slate-500 mb-5">
          Email or text a single user, everyone on the platform, or a filtered group.
        </p>

        <div className="flex gap-2 mb-5 border-b">
          <button
            onClick={() => setTab('compose')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'compose' ? 'border-cyan-600 text-cyan-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="broadcast-tab-compose"
          >
            Compose
          </button>
          <button
            onClick={() => setTab('history')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === 'history' ? 'border-cyan-600 text-cyan-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="broadcast-tab-history"
          >
            <History className="inline w-4 h-4 mr-1" /> History
          </button>
        </div>

        {tab === 'compose' && (
          <div className="grid gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">1. Target</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Target audience</label>
                  <Select value={targetType} onValueChange={(v) => { setTargetType(v); setPreviewStats(null); }}>
                    <SelectTrigger data-testid="broadcast-target-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TARGET_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {targetType === 'users' && (
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-slate-600 block">
                      Recipients
                      {selectedUsers.length > 0 && (
                        <span className="ml-2 text-[11px] text-slate-400">({selectedUsers.length} selected)</span>
                      )}
                    </label>
                    <UserTypeahead
                      value={selectedUsers}
                      onChange={(next) => { setSelectedUsers(next); setPreviewStats(null); }}
                      placeholder="Search by name, email, or phone…"
                      data-testid="broadcast-user-picker"
                    />
                    <p className="text-[11px] text-slate-400">
                      Start typing and pick one or more users. Chips can be removed individually.
                    </p>
                  </div>
                )}

                {targetType === 'filtered' && (
                  <div className="grid md:grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-slate-500">Roles (comma-separated)</label>
                      <Input value={filterRoles} onChange={(e) => setFilterRoles(e.target.value)} placeholder="talent, client" data-testid="broadcast-filter-roles"/>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">States (comma-separated)</label>
                      <Input value={filterStates} onChange={(e) => setFilterStates(e.target.value)} placeholder="TX, CA" data-testid="broadcast-filter-states"/>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Cities (comma-separated)</label>
                      <Input value={filterCities} onChange={(e) => setFilterCities(e.target.value)} placeholder="Houston, Austin" data-testid="broadcast-filter-cities"/>
                    </div>
                    <p className="md:col-span-3 text-xs text-slate-400">
                      City / state apply to talent only (clients don't carry those columns).
                    </p>
                  </div>
                )}

                <Button variant="outline" onClick={runPreview} disabled={previewing} data-testid="broadcast-preview-btn">
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Eye className="w-4 h-4 mr-1"/>}
                  Preview recipient count
                </Button>

                {previewStats && (
                  <div className="bg-cyan-50 border border-cyan-200 rounded-md p-3 text-sm" data-testid="broadcast-preview-result">
                    <p><strong className="text-cyan-700">{previewStats.total}</strong> recipients match</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {previewStats.with_email} with email · {previewStats.with_phone} with phone
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">2. Channel</CardTitle></CardHeader>
              <CardContent>
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger data-testid="broadcast-channel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHANNEL_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">3. Message</CardTitle>
                <Select onValueChange={applyTemplate}>
                  <SelectTrigger className="w-56 text-xs" data-testid="broadcast-template"><SelectValue placeholder="Apply template…"/></SelectTrigger>
                  <SelectContent>
                    {TEMPLATES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent className="space-y-3">
                {(channel === 'email' || channel === 'both') && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Email subject</label>
                      <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="broadcast-subject"/>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">
                        Email body (HTML allowed) — <code>{'{{first_name}}'}</code>, <code>{'{{app_url}}'}</code> supported
                      </label>
                      <Textarea rows={10} value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)} data-testid="broadcast-body-html"/>
                    </div>
                  </>
                )}
                {(channel === 'sms' || channel === 'both') && (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">
                      SMS body ({bodyText.length}/480 chars — {'{{first_name}}'} + {'{{app_url}}'} supported)
                    </label>
                    <Textarea rows={4} value={bodyText} onChange={(e) => setBodyText(e.target.value)} data-testid="broadcast-body-text" maxLength={480}/>
                  </div>
                )}
                {channel === 'email' && (
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1 block">Plain-text fallback (optional, helps deliverability)</label>
                    <Textarea rows={3} value={bodyText} onChange={(e) => setBodyText(e.target.value)} data-testid="broadcast-body-text-plain"/>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button
                onClick={doSend}
                disabled={sending || !previewStats}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="broadcast-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1"/> : <Send className="w-4 h-4 mr-1"/>}
                Send to {previewStats?.total ?? '?'} recipients
              </Button>
            </div>
          </div>
        )}

        {tab === 'history' && (
          <Card>
            <CardHeader><CardTitle className="text-base">Broadcast history</CardTitle></CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400"/></div>
              ) : history.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">No broadcasts sent yet.</p>
              ) : (
                <div className="divide-y">
                  {history.map(b => (
                    <div key={b.id} className="py-3 flex items-start justify-between gap-4" data-testid={`broadcast-history-row-${b.id}`}>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {b.channel === 'email' && <Mail className="w-4 h-4 text-cyan-600"/>}
                          {b.channel === 'sms' && <MessageSquare className="w-4 h-4 text-emerald-600"/>}
                          {b.channel === 'both' && <><Mail className="w-4 h-4 text-cyan-600"/><MessageSquare className="w-4 h-4 text-emerald-600"/></>}
                          <p className="font-medium text-sm">{b.subject || '(SMS-only)'}</p>
                        </div>
                        <p className="text-xs text-slate-500">
                          <Users className="inline w-3 h-3 mr-0.5"/> {b.target_type} · {b.recipient_count} recipients · by {b.sent_by_email}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5">{formatDateMMDDYYYY(b.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3 mr-0.5"/>{b.sent_count}
                          </Badge>
                          {b.failed_count > 0 && (
                            <Badge className="bg-red-50 text-red-700 border border-red-200">
                              <XCircle className="w-3 h-3 mr-0.5"/>{b.failed_count}
                            </Badge>
                          )}
                          {b.skipped_count > 0 && (
                            <Badge
                              className="bg-amber-50 text-amber-800 border border-amber-200"
                              title="Skipped: invalid or placeholder phone (no Twilio call made)"
                              data-testid={`broadcast-history-skipped-${b.id}`}
                            >
                              {b.skipped_count} skipped
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Failure detail modal — per-recipient error messages for a broadcast */}
      <Dialog open={!!detailBroadcast} onOpenChange={(v) => !v && setDetailBroadcast(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="broadcast-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Delivery failures
              {detailBroadcast && (
                <Badge variant="outline" className="ml-2 text-xs">{detailBroadcast.failed_count} failed · {detailBroadcast.sent_count} sent</Badge>
              )}
            </DialogTitle>
          <DialogDescription className="sr-only">Delivery failures</DialogDescription>
          </DialogHeader>
          {detailLoading && <Loader2 className="w-5 h-5 animate-spin text-cyan-600" />}
          {detailBroadcast && (
            <div className="space-y-3">
              <div className="text-xs text-gray-600">
                <div><span className="font-medium">Subject:</span> {detailBroadcast.subject || '—'}</div>
                <div><span className="font-medium">Target:</span> {detailBroadcast.target_type} · {detailBroadcast.recipient_count} recipients</div>
                <div><span className="font-medium">Sent by:</span> {detailBroadcast.sent_by_email}</div>
              </div>
              <div className="border-t pt-3">
                <h4 className="text-sm font-medium mb-2">Failed recipients</h4>
                <div className="space-y-2 text-xs">
                  {detailBroadcast.recipients?.filter((r) => r.status === 'failed').length === 0 && (
                    <p className="text-gray-500 italic">No failures recorded.</p>
                  )}
                  {detailBroadcast.recipients?.filter((r) => r.status === 'failed').map((r) => (
                    <div key={r.id} className="bg-red-50 border border-red-200 rounded p-2">
                      <div className="font-medium text-red-900">{r.email || r.phone || '(no contact)'}</div>
                      <div className="text-red-800 whitespace-pre-wrap">{r.error || 'No error message recorded'}</div>
                      {r.sent_at && <div className="text-red-600 mt-1">{new Date(r.sent_at).toLocaleString()}</div>}
                    </div>
                  ))}
                </div>
                {detailBroadcast.recipients?.filter((r) => r.status === 'skipped').length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium mb-2 text-amber-700">Skipped</h4>
                    <div className="space-y-1 text-xs">
                      {detailBroadcast.recipients.filter((r) => r.status === 'skipped').slice(0, 20).map((r) => (
                        <div key={r.id} className="bg-amber-50 border border-amber-200 rounded p-1.5">
                          <span className="font-medium">{r.email || r.phone || '(no contact)'}</span>
                          {r.error && <span className="text-amber-800"> — {r.error}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBroadcast;
